import * as React from 'react';
import { Dispatch } from 'react-redux';
import { StoreState } from '../../reducers';
import Utils from '../../utils';
import MultiAppAuthenticationPopup from '../../containers/MultiAppAuthentication/Popup';
import Loader, { LoaderType, LoaderStepName, LoaderStep } from '../Loader';
import MultiAppAuthenticationForm from '../../containers/MultiAppAuthentication/Form';
import { MultiAppAuthenticationCredentials } from '../../containers/MultiAppAuthentication/reducer';
import {
	StyledMultiAppAuthentication, MultiAppAuthenticationTitle, WebpublicationLogo, SwitchToDemoViewBlock, SwitchLoginViewBlock,
	BackLink, SwitchToDemoFormButton, MultiAppAuthenticationContainer, FormWrapper, MultiAppAuthenticationMethodButton, MultiAppAuthenticationMethods
} from '../../components/MultiAppAuthentication/styles';
import { SamlIframeOrigin, SamlAuthenticationIframe } from '../Authentication/styles';
import { AUTH_METHODS, MultiAppAuthenticationViewMode, AuthMethod } from '../../containers/MultiAppAuthentication/constants';
import { Translate } from 'react-redux-i18n';
import UrlUtils from '../../utils/url';
import { signIn } from '../../containers/MultiAppAuthentication/actions';
import ApiUtils from '../../utils/api';
import LanguageSwitcher from '../../containers/LanguageSwitcher';
import AuthUtils from '../../utils/authentication';
import { Api } from '../../typings/api';

export interface MultiAppAuthenticationProps {
	accessibleApps: Api.AspDriveResourceItem[];
	credentials: MultiAppAuthenticationCredentials;
	galleryConfig: Api.Config;
	orgAuthMethods: AuthMethod[];
	login: string;
	userLocale: string; // do not remove. Prop is used to update locale through rerender
	dispatch: Dispatch<StoreState>;
}

interface MultiAppAuthenticationState {
	isSelectAppPopupOpen: boolean;
	shouldRememberCredentials: boolean;
	viewMode: MultiAppAuthenticationViewMode;
	hasLoader: boolean;
	method?: AuthMethod;
}

export class MultiAppAuthentication extends React.Component<MultiAppAuthenticationProps, MultiAppAuthenticationState> {

	constructor(props: MultiAppAuthenticationProps) {
		super(props);
		const { accessibleApps } = props;

		this.state = {
			isSelectAppPopupOpen: accessibleApps.length > 1,
			shouldRememberCredentials: true,
			viewMode: MultiAppAuthenticationViewMode.Login,
			hasLoader: false
		};
	}

	componentWillMount() {
		window.addEventListener('message', this.handleOrgAuthentication);
	}

	componentWillReceiveProps(nextProps: MultiAppAuthenticationProps) {
		if (this.props.accessibleApps !== nextProps.accessibleApps) {
			this.setState({ isSelectAppPopupOpen: nextProps.accessibleApps.length > 1 });
		}
	}

	componentWillUnMount() {
		window.removeEventListener('message', this.handleOrgAuthentication);
	}

	handleOrgAuthentication = (e: MessageEvent) => {
		const data: string = e.data;
		const { dispatch, credentials } = this.props;
		const { method } = this.state;
		const adminToken = 'wp_admin_token';

		if (typeof (data) === 'string' && data.indexOf(adminToken) === 0) {
			const company = credentials && credentials.company ? credentials.company : '';
			const token = data.slice(data.indexOf('=') + 1);
			dispatch(signIn({ token, company, method }, true));
		}
	}

	onPopupClosed = (): void => {
		this.setState({ isSelectAppPopupOpen: false });
	}

	selectAuthMethod(method: AuthMethod): void {
		this.setState({ method });

		if (method === AuthMethod.Webpublication) {
			return this.switchToLoginView();
		} else {
			return this.switchToOrgLoginView();
		}
	}

	switchToDemoView = (): void => {
		this.switchViewMode(MultiAppAuthenticationViewMode.Creation);
	}

	switchToLoginView = (): void => {
		this.switchViewMode(MultiAppAuthenticationViewMode.Login);
	}

	switchToCompanyView = (): void => {
		this.switchViewMode(MultiAppAuthenticationViewMode.Company);
	}

	switchToOrgLoginView(): void {
		this.switchViewMode(MultiAppAuthenticationViewMode.OrgLogin);
	}

	switchViewMode = (viewMode: MultiAppAuthenticationViewMode): void => {
		this.setState({ viewMode });
	}

	setLoader = (hasLoader: boolean): void => {
		this.setState({ hasLoader });
	}

	get isLoginView(): boolean {
		return AuthUtils.isLoginView(this.state.viewMode);
	}

	get isOrgLoginView(): boolean {
		return AuthUtils.isOrgLoginView(this.state.viewMode);
	}

	get isPasswordView(): boolean {
		return AuthUtils.isPasswordView(this.state.viewMode);
	}

	get isCompanyView(): boolean {
		return AuthUtils.isCompanyView(this.state.viewMode);
	}

	get isMethodView(): boolean {
		return AuthUtils.isMethodView(this.state.viewMode);
	}

	get isCreationView(): boolean {
		return AuthUtils.isCreationView(this.state.viewMode);
	}

	get viewTextTitle(): string {
		const { isLoginView, isCompanyView, isMethodView, isPasswordView } = this;

		if (isCompanyView) {
			return 'messages.your_company_account';
		} else if (isLoginView || isMethodView) {
			return 'messages.or_sign_in';
		} else if (isPasswordView) {
			return 'messages.enter_password';
		}

		return '';
	}

	renderMethodButtons = () => {
		const { orgAuthMethods } = this.props;

		return orgAuthMethods.map(method => {
			return (
				<MultiAppAuthenticationMethodButton onClick={() => this.selectAuthMethod(method)} key={`button-${method}`}>
					<Translate value={`${AUTH_METHODS[method].text}`} />
				</MultiAppAuthenticationMethodButton>
			);
		});
	}

	renderBackButtonBlock() {
		const { isMethodView, isCreationView, isPasswordView } = this;

		return (
			<SwitchLoginViewBlock isCreationView={isCreationView} onClick={isMethodView ? this.switchToCompanyView : this.switchToLoginView}>
				<BackLink>{Utils.getSvgIcon('iconset_back')}
					{isPasswordView
						? this.props.login
						: <Translate value={`messages.back_to_${isMethodView ? 'company' : 'login'}`} />
					}
				</BackLink>
			</SwitchLoginViewBlock>
		);
	}

	renderAuthenticationBlock() {
		const { isMethodView, isOrgLoginView, isLoginView } = this;
		const { viewMode } = this.state;
		const showBackButton = !isLoginView && !isOrgLoginView;

		return (
			<>
				<LanguageSwitcher />
				<MultiAppAuthenticationContainer>
					<WebpublicationLogo>{Utils.getSvgIcon('uprez')}</WebpublicationLogo>
					<FormWrapper>
						{isLoginView &&
							<SwitchToDemoViewBlock>
								<SwitchToDemoFormButton onClick={this.switchToDemoView}>
									<Translate value="messages.test_our_demo" />
								</SwitchToDemoFormButton>
							</SwitchToDemoViewBlock>
						}
						{showBackButton &&
							this.renderBackButtonBlock()
						}
						{this.viewTextTitle &&
							<MultiAppAuthenticationTitle>
								<Translate value={this.viewTextTitle} />
							</MultiAppAuthenticationTitle>
						}
						{!isMethodView ?
							<MultiAppAuthenticationForm
								viewMode={viewMode}
								switchViewMode={this.switchViewMode}
								setLoader={this.setLoader}
							/>
							: <MultiAppAuthenticationMethods>
								{this.renderMethodButtons()}
							</MultiAppAuthenticationMethods>
						}
					</FormWrapper>
				</MultiAppAuthenticationContainer>
			</>
		);
	}

	render() {
		const { isSelectAppPopupOpen, shouldRememberCredentials, hasLoader } = this.state;
		const { credentials, galleryConfig } = this.props;
		const server = ApiUtils.multiAppAuthenticationServer;
		const { isOrgLoginView, isCreationView, isPasswordView } = this;
		const company = credentials && credentials.company ? credentials.company : '';
		const showLoader = hasLoader && (isCreationView || isPasswordView);
		const loaderStep: LoaderStep = isCreationView ? { step: LoaderStepName.Creating } : { step: LoaderStepName.LoggingIn };

		return (
			<StyledMultiAppAuthentication>
				{isOrgLoginView &&
					<SamlAuthenticationIframe key="messageinterceptor-iframe" origin={SamlIframeOrigin.App} src={UrlUtils.getMessageInterceptorUrl(server)} />
				}
				{showLoader &&
					<Loader loaderStep={loaderStep} type={LoaderType.Overlay} />
				}
				{isSelectAppPopupOpen &&
					<MultiAppAuthenticationPopup
						onClose={this.onPopupClosed}
						shouldSaveGalleryConfig={shouldRememberCredentials}
					/>
				}
				{isOrgLoginView
					? <SamlAuthenticationIframe key="login-iframe" origin={SamlIframeOrigin.Authentication} src={UrlUtils.getOrgLoginUrl(galleryConfig, company)} />
					: this.renderAuthenticationBlock()
				}
			</StyledMultiAppAuthentication>
		);
	}
}

export default MultiAppAuthentication;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/MultiAppAuthentication/index.tsx